import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { PublicShell } from './PublicShell';
import {
  DOC_INDEX,
  DOC_PAGES,
  type Block,
  type CodeLine,
  type DocPage,
  type Sample,
} from './docsContent';

function Code({ lines }: { lines: readonly CodeLine[] }) {
  return (
    // tabIndex on the scroller: a keyboard user has to be able to reach a
    // horizontally scrolling region to scroll it.
    <pre className="code-body" tabIndex={0}>
      <code>
        {lines.map((line, i) => (
          <span key={i}>
            {line.t ? <span className={`c-${line.t}`}>{line.s}</span> : line.s}
            {'\n'}
          </span>
        ))}
      </code>
    </pre>
  );
}

/** Language tabs over one sample. A real tablist with arrow-key movement -- the
 *  same treatment the landing page uses, for the same reason: something
 *  announced as a tab has to behave like one. */
function Tabs({ samples }: { samples: readonly Sample[] }) {
  const [active, setActive] = useState(0);

  if (samples.length === 1) {
    return (
      <div className="doc-snippet">
        <div className="doc-snippet-head">{samples[0].label}</div>
        <Code lines={samples[0].lines} />
      </div>
    );
  }

  return (
    <div className="doc-snippet">
      <div
        className="code-tabs"
        role="tablist"
        aria-label="Language"
        onKeyDown={(event) => {
          const delta =
            event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
          if (!delta) return;
          event.preventDefault();
          setActive((current) => (current + delta + samples.length) % samples.length);
        }}
      >
        {samples.map((sample, index) => (
          <button
            key={sample.label}
            type="button"
            role="tab"
            aria-selected={index === active}
            tabIndex={index === active ? 0 : -1}
            className={index === active ? 'code-tab active' : 'code-tab'}
            onClick={() => setActive(index)}
          >
            {sample.label}
          </button>
        ))}
      </div>
      <Code lines={samples[active].lines} />
    </div>
  );
}

/** The plan limits table, read from the live catalogue.
 *
 *  Renders nothing at all rather than a partial or stale table if the fetch
 *  fails: a documented limit somebody plans against has to be the real one, and
 *  a placeholder row would be read as fact.
 */
function PlanLimits() {
  const [rows, setRows] = useState<string[][] | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch('/v1/pricing');
        if (!response.ok) throw new Error(String(response.status));
        const body = (await response.json()) as {
          plans: {
            label: string;
            price_cents: number | null;
            included_volume: number;
            custom_domains: number | null;
            team_seats: number | null;
          }[];
        };
        if (!live) return;
        setRows(
          body.plans.map((plan) => [
            plan.label,
            plan.price_cents === null
              ? 'Negotiated'
              : plan.included_volume.toLocaleString(),
            plan.custom_domains === null ? 'Unlimited' : String(plan.custom_domains),
            plan.team_seats === null ? 'Unlimited' : String(plan.team_seats),
          ]),
        );
      } catch {
        if (live) setRows([]);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (rows === null) return <p className="doc-note">Loading plan limits…</p>;
  if (rows.length === 0) {
    return (
      <p className="doc-note">
        Plan limits are not loading right now — see the{' '}
        <a href="/pricing">pricing page</a>.
      </p>
    );
  }

  return (
    <div className="doc-table-scroll">
      <table className="doc-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Emails / month</th>
            <th>Domains</th>
            <th>Seats</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]}>
              <th scope="row">
                <code>{row[0]}</code>
              </th>
              {row.slice(1).map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Blocks({ blocks }: { blocks: readonly Block[] }) {
  return (
    <>
      {blocks.map((block, i) => {
        switch (block.kind) {
          case 'h2':
            return <h2 key={i}>{block.body}</h2>;
          case 'p':
            return <p key={i}>{block.body}</p>;
          case 'note':
            return (
              <p className="doc-note" key={i}>
                {block.body}
              </p>
            );
          case 'list':
            return (
              <ul className="doc-list" key={i}>
                {block.items.map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            );
          case 'chips':
            return (
              <ul className="doc-chips" key={i}>
                {block.items.map((item) => (
                  <li key={item}>
                    <code>{item}</code>
                  </li>
                ))}
              </ul>
            );
          case 'code':
            return (
              <div className="doc-snippet" key={i}>
                <div className="doc-snippet-head">{block.title}</div>
                <Code lines={block.lines} />
              </div>
            );
          case 'tabs':
            return <Tabs key={i} samples={block.samples} />;
          case 'plan-limits':
            return <PlanLimits key={i} />;
          case 'table':
            return (
              // Wide content scrolls inside its own box, so the page body never
              // scrolls sideways.
              <div className="doc-table-scroll" key={i}>
                <table className="doc-table">
                  <thead>
                    <tr>
                      {block.head.map((cell) => (
                        <th key={cell}>{cell}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((row) => (
                      <tr key={row[0]}>
                        {row.map((cell, j) =>
                          j === 0 ? (
                            <th scope="row" key={j}>
                              <code>{cell}</code>
                            </th>
                          ) : (
                            <td key={j}>{cell}</td>
                          ),
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
        }
      })}
    </>
  );
}

function Sidebar({ current }: { current: string }) {
  return (
    <nav className="doc-nav" aria-label="Documentation">
      <div className="doc-nav-title">Guides</div>
      <ul>
        {DOC_INDEX.map((entry) => (
          <li key={entry.slug}>
            <Link
              to={`/docs/${entry.slug}`}
              className={entry.slug === current ? 'doc-nav-link current' : 'doc-nav-link'}
              aria-current={entry.slug === current ? 'page' : undefined}
            >
              {entry.title}
            </Link>
          </li>
        ))}
      </ul>
      <div className="doc-nav-title">Reference</div>
      <ul>
        <li>
          {/* Served by the API, not this app, so a real navigation rather than a
              router link. */}
          <a className="doc-nav-link" href="/reference">
            API reference
          </a>
        </li>
      </ul>
    </nav>
  );
}

export function Docs() {
  const { slug } = useParams();
  const current = slug ?? DOC_PAGES[0].slug;
  const page: DocPage | undefined = DOC_PAGES.find((entry) => entry.slug === current);

  // An unknown slug goes to the first page rather than rendering an empty shell.
  if (!page) return <Navigate to="/docs" replace />;

  const index = DOC_PAGES.indexOf(page);
  const previous = DOC_PAGES[index - 1];
  const next = DOC_PAGES[index + 1];

  return (
    <PublicShell>
      <div className="doc-layout">
        <Sidebar current={page.slug} />

        <article className="doc-article">
          <span className="eyebrow">Documentation</span>
          <h1>{page.title}</h1>
          <p className="doc-summary">{page.summary}</p>

          <Blocks blocks={page.blocks} />

          <nav className="doc-pager" aria-label="Pagination">
            {previous ? (
              <Link className="doc-pager-link" to={`/docs/${previous.slug}`}>
                <span>Previous</span>
                {previous.title}
              </Link>
            ) : (
              <span />
            )}
            {next && (
              <Link className="doc-pager-link next" to={`/docs/${next.slug}`}>
                <span>Next</span>
                {next.title}
              </Link>
            )}
          </nav>
        </article>
      </div>
    </PublicShell>
  );
}
